import { useMutation, useQuery } from '@apollo/client/react'
import { Button, Flex, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { DataTable } from '../../../components/DataTable'
import { showToast } from '../../../components/Toast/showToast'
import type {
  CustomerDataApiDeployment,
  DeploymentStatus,
} from '../../../services/customerDataApiService'
import {
  cancelCustomerDataApiDeploymentMutation,
  customerDataApiDeploymentsQuery,
  rerunCustomerDataApiDeploymentMutation,
} from '../../../services/customerDataApiService'
import {
  ConfigIdFilter,
  StatusFilterPopover,
  isTerminalStatus,
  useDeploymentsAdminColumns,
} from './DeploymentsAdminPage.parts'

export const DeploymentsAdminPage = () => {
  const [statusFilter, setStatusFilter] = useState<DeploymentStatus[]>([])
  const [configIdFilter, setConfigIdFilter] = useState<string>('')

  const filter = useMemo(() => {
    const f: Record<string, unknown> = {}
    if (statusFilter.length > 0) {
      f.status = { in: statusFilter }
    }
    if (configIdFilter.trim()) {
      f.configId = { equals: configIdFilter.trim() }
    }
    return Object.keys(f).length > 0 ? f : undefined
  }, [statusFilter, configIdFilter])

  const listVariables = useMemo(
    () => ({
      first: 100,
      skip: 0,
      filter,
      orderBy: [{ startedAt: 'DESC' as const }],
    }),
    [filter],
  )

  const [deploymentsPollMs, setDeploymentsPollMs] = useState(0)

  const { data, loading: isLoading } = useQuery(
    customerDataApiDeploymentsQuery,
    {
      variables: listVariables,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      pollInterval: deploymentsPollMs,
    },
  )

  const deployments = ((
    data as { CustomerDataApiDeployment_list?: { items?: unknown } }
  )?.CustomerDataApiDeployment_list?.items ?? []) as CustomerDataApiDeployment[]

  useEffect(() => {
    setDeploymentsPollMs(
      deployments.some(d => !isTerminalStatus(d.status as DeploymentStatus))
        ? 2000
        : 0,
    )
  }, [deployments])

  const [rerunMutation, { loading: rerunMutationLoading }] = useMutation(
    rerunCustomerDataApiDeploymentMutation,
    {
      refetchQueries: [
        { query: customerDataApiDeploymentsQuery, variables: listVariables },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showToast({
          title: 'Re-run started',
          description: 'A new pipeline run was created and is running.',
          variant: 'success',
        })
      },
      onError: err =>
        showToast({
          title: 'Re-run failed',
          description: err.message,
          variant: 'error',
        }),
    },
  )

  const [cancelMutation, { loading: cancelMutationLoading }] = useMutation(
    cancelCustomerDataApiDeploymentMutation,
    {
      refetchQueries: [
        { query: customerDataApiDeploymentsQuery, variables: listVariables },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showToast({
          title: 'Cancellation requested',
          description:
            'The pipeline will abort at the next safe checkpoint and roll back to the previous live config automatically.',
          variant: 'success',
        })
      },
      onError: err =>
        showToast({
          title: 'Cancel failed',
          description: err.message,
          variant: 'error',
        }),
    },
  )

  const columns = useDeploymentsAdminColumns({
    cancelMutation,
    rerunMutation,
    cancelMutationLoading,
    rerunMutationLoading,
  })

  return (
    <AdminPageLayout pageTitle="Deployments">
      <Text mb={4} color="presets.neutral.70">
        Pipeline runs for the Customer Data API. Cancel an in-flight run, re-run
        a past one, or drill into a specific run for logs and SMRT artifact
        details.
      </Text>

      <Flex gap="2" mb="3" align="center" flexWrap="wrap">
        <StatusFilterPopover
          selected={statusFilter}
          onChange={setStatusFilter}
        />
        <ConfigIdFilter value={configIdFilter} onChange={setConfigIdFilter} />
        {(statusFilter.length > 0 || configIdFilter) && (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              setStatusFilter([])
              setConfigIdFilter('')
            }}
          >
            Clear filters
          </Button>
        )}
      </Flex>

      <DataTable
        data={deployments}
        columns={columns}
        isLoading={isLoading}
        height="calc(100vh - 240px)"
      />
    </AdminPageLayout>
  )
}
